import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Mail, Check, AlertCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Unsubscribe = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState({
    email: '',
    unsubscribe_all: false,
    unsubscribe_marketing: false,
    unsubscribe_sequences: false,
    unsubscribe_order_updates: false
  });
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/emails/unsubscribe/${token}`);
        setPreferences({
          email: res.data.email,
          unsubscribe_all: res.data.unsubscribe_all || false,
          unsubscribe_marketing: res.data.unsubscribe_marketing || false,
          unsubscribe_sequences: res.data.unsubscribe_sequences || false,
          unsubscribe_order_updates: res.data.unsubscribe_order_updates || false
        });
      } catch (err) {
        setError('Invalid or expired unsubscribe link');
      } finally {
        setLoading(false);
      }
    };
    fetchPreferences();
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/emails/unsubscribe/${token}`, preferences);
      setSuccess(true);
      toast.success('Your preferences have been updated');
    } catch (err) {
      toast.error('Failed to update preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleUnsubscribeAll = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/emails/unsubscribe/${token}`, {
        ...preferences,
        unsubscribe_all: true
      });
      setSuccess(true);
      setPreferences(prev => ({ ...prev, unsubscribe_all: true }));
      toast.success('You have been unsubscribed from all emails');
    } catch (err) {
      toast.error('Failed to unsubscribe');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-[#003456] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#0a0203] mb-2">Invalid Link</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link to="/">
            <Button className="bg-[#003456]">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Homepage
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (success && preferences.unsubscribe_all) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-[#0a0203] mb-2">Unsubscribed</h1>
          <p className="text-gray-600 mb-2">
            You have been unsubscribed from all D-Freeze emails.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            We're sorry to see you go. If you change your mind, you can always subscribe again.
          </p>
          <Link to="/">
            <Button className="bg-[#003456]">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Homepage
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-lg w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#003456] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Mail className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-[#0a0203] mb-2">Email Preferences</h1>
          <p className="text-gray-600">
            Manage your email preferences for <strong>{preferences.email}</strong>
          </p>
        </div>

        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center gap-3">
            <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
            <p className="text-green-700 text-sm">Your preferences have been saved!</p>
          </div>
        )}

        <div className="space-y-4 mb-8">
          <label className="flex items-start gap-4 p-4 border rounded-xl hover:bg-gray-50 cursor-pointer">
            <input
              type="checkbox"
              checked={preferences.unsubscribe_marketing}
              onChange={(e) => setPreferences(prev => ({ ...prev, unsubscribe_marketing: e.target.checked }))}
              className="mt-1 h-5 w-5 rounded border-gray-300"
            />
            <div>
              <p className="font-medium text-[#0a0203]">Marketing Emails</p>
              <p className="text-sm text-gray-500">Promotional campaigns, sales, and special offers</p>
            </div>
          </label>

          <label className="flex items-start gap-4 p-4 border rounded-xl hover:bg-gray-50 cursor-pointer">
            <input
              type="checkbox"
              checked={preferences.unsubscribe_sequences}
              onChange={(e) => setPreferences(prev => ({ ...prev, unsubscribe_sequences: e.target.checked }))}
              className="mt-1 h-5 w-5 rounded border-gray-300"
            />
            <div>
              <p className="font-medium text-[#0a0203]">Welcome & Tutorial Sequences</p>
              <p className="text-sm text-gray-500">Automated welcome emails, tutorials, and tips</p>
            </div>
          </label>

          <label className="flex items-start gap-4 p-4 border rounded-xl hover:bg-gray-50 cursor-pointer">
            <input
              type="checkbox"
              checked={preferences.unsubscribe_order_updates}
              onChange={(e) => setPreferences(prev => ({ ...prev, unsubscribe_order_updates: e.target.checked }))}
              className="mt-1 h-5 w-5 rounded border-gray-300"
            />
            <div>
              <p className="font-medium text-[#0a0203]">Order Updates</p>
              <p className="text-sm text-gray-500">Order confirmations and shipping notifications</p>
            </div>
          </label>
        </div>

        <div className="space-y-3">
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="w-full bg-[#003456] hover:bg-[#002B47]"
          >
            {saving ? 'Saving...' : 'Save Preferences'}
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white px-4 text-gray-500">or</span>
            </div>
          </div>

          <Button 
            onClick={handleUnsubscribeAll}
            disabled={saving}
            variant="outline"
            className="w-full border-red-300 text-red-600 hover:bg-red-50"
          >
            Unsubscribe from All Emails
          </Button>
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          You can update these preferences at any time by clicking the unsubscribe link in any of our emails.
        </p>
      </div>
    </div>
  );
};

export default Unsubscribe;
